<template>
    <div class="space-y-3">
        <div class="flex gap-1 rounded-md bg-gray-100 p-1">
            <button
                v-for="m in modes"
                :key="m.value"
                type="button"
                class="flex-1 rounded px-2 py-1 text-xs font-medium transition-colors"
                :class="draftMode === m.value ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
                @click="draftMode = m.value"
            >
                {{ m.label }}
            </button>
        </div>

        <div class="flex flex-wrap gap-1">
            <button
                v-for="p in presets"
                :key="p.label"
                type="button"
                class="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-600 hover:bg-gray-200"
                @click="applyPreset(p)"
            >
                {{ p.label }}
            </button>
        </div>

        <div class="flex flex-col gap-2">
            <div v-if="draftMode !== 'before'">
                <span v-if="draftMode === 'between'" class="mb-1 block text-xs text-gray-500">From</span>
                <UInput v-model="draftStart" type="date" size="lg" class="w-full" />
            </div>
            <div v-if="draftMode !== 'after'">
                <span v-if="draftMode === 'between'" class="mb-1 block text-xs text-gray-500">To</span>
                <UInput v-model="draftEnd" type="date" size="lg" class="w-full" />
            </div>
        </div>

        <UButton block size="sm" color="black" @click="apply">Apply</UButton>
    </div>
</template>

<script setup lang="ts">
import { sub, format } from 'date-fns'
import type { Filter } from '~/types/filter'

const emit = defineEmits<{ 'update:filter': [Filter] }>()
const props = defineProps<{ filter: Filter }>()

const v = props.filter.value ?? {}
const draftMode = ref<'after' | 'before' | 'between'>(v.start && v.end ? 'between' : v.end ? 'before' : 'after')
const draftStart = ref<string>(v.start ?? '')
const draftEnd = ref<string>(v.end ?? '')

const modes = [
    { value: 'after' as const, label: 'After' },
    { value: 'before' as const, label: 'Before' },
    { value: 'between' as const, label: 'Between' },
]

const presets = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'Last year', days: 365 },
]

function fmt(d: Date) { return format(d, 'yyyy-MM-dd') }

function applyPreset(p: { days: number }) {
    draftMode.value = 'between'
    draftStart.value = fmt(sub(new Date(), { days: p.days }))
    draftEnd.value = fmt(new Date())
    apply()
}

function apply() {
    const start = draftMode.value !== 'before' ? (draftStart.value || undefined) : undefined
    const end = draftMode.value !== 'after' ? (draftEnd.value || undefined) : undefined
    if (!start && !end) emit('update:filter', { ...props.filter, value: undefined, selected: undefined })
    else emit('update:filter', { ...props.filter, value: { start, end }, selected: { start, end } })
}
</script>
